import { useEffect, useState } from "react";
import { api } from "@/lib/kfmApi";
import { Loader2, HelpCircle, AlertTriangle, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

// KB self-audit: topics users asked the Help Assistant about that its knowledge base
// didn't cover. Repeated asks (3+) are flagged so the KB can be filled in.
export default function HelpKbGaps() {
  const [gaps, setGaps] = useState(null);
  const [repeatedCount, setRepeatedCount] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/help/kb-gaps");
      setGaps(data.gaps || []);
      setRepeatedCount(data.repeated_count || 0);
    } catch {
      setGaps([]);
    }
  };
  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    try {
      await api.delete(`/admin/help/kb-gaps/${encodeURIComponent(id)}`);
      setGaps((g) => {
        const next = (g || []).filter((x) => x.id !== id);
        setRepeatedCount(next.filter((x) => x.repeated).length);
        return next;
      });
      toast.success("Gap dismissed");
    } catch {
      toast.error("Couldn't dismiss");
    }
  };

  if (gaps === null) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} /></div>;
  }

  return (
    <div data-testid="admin-help-gaps" className="space-y-5">
      <header className="space-y-1">
        <h2 className="text-2xl text-white">Help Assistant — knowledge gaps</h2>
        <p className="text-sm text-slate-400 leading-relaxed">
          Questions people asked that the Help Assistant couldn&rsquo;t answer from its knowledge base.
          {repeatedCount > 0 && (
            <span className="text-[#B45309] font-medium"> {repeatedCount} topic{repeatedCount === 1 ? "" : "s"} asked repeatedly — worth adding to the KB.</span>
          )}
        </p>
      </header>

      {gaps.length === 0 ? (
        <div className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-10 text-center space-y-2" data-testid="help-gaps-empty">
          <HelpCircle className="w-8 h-8 mx-auto text-slate-500" strokeWidth={1.5} />
          <p className="text-slate-400">No knowledge gaps logged yet.</p>
          <p className="text-sm text-slate-500">When users ask about something the assistant doesn&rsquo;t know, it&rsquo;ll show up here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="help-gaps-list">
          {gaps.map((g) => (
            <div
              key={g.id}
              data-testid={`help-gap-${g.id}`}
              className={`rounded-2xl border p-4 space-y-2 ${g.repeated ? "bg-amber-500/10 border-amber-500/30" : "bg-[#0E131F] border-[#1E273D]"}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {g.repeated && <AlertTriangle className="w-4 h-4 text-[#B45309] shrink-0" strokeWidth={1.75} />}
                  <span className="font-medium text-slate-200 capitalize truncate">{g.topic}</span>
                  <span
                    data-testid={`help-gap-count-${g.id}`}
                    className={`shrink-0 text-[11px] font-semibold rounded-full px-2 py-0.5 ${g.repeated ? "bg-[#B45309] text-white" : "bg-[#141B29] text-slate-400"}`}
                  >
                    asked {g.count}×
                  </span>
                </div>
                <button
                  data-testid={`help-gap-dismiss-${g.id}`}
                  onClick={() => dismiss(g.id)}
                  title="Dismiss (KB updated or not needed)"
                  className="shrink-0 p-1.5 text-slate-500 hover:text-[#00E5FF] transition-colors"
                >
                  <Check className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </div>
              {(g.examples || []).length > 0 && (
                <ul className="text-sm text-slate-400 leading-relaxed list-disc pl-5 space-y-0.5">
                  {g.examples.slice(-3).map((ex, i) => <li key={i} className="truncate">{ex}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
